import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Command } from 'cmdk';
import {
  Activity,
  Building2,
  ClipboardList,
  PhoneCall,
  Repeat,
  Search,
  Settings,
  Sparkles,
  UserPlus,
  Users,
} from 'lucide-react';
import { Button, DialogDescription, DialogTitle, cn } from '@command-center/ui-system';
import { leadloopApi } from '../lib/leadloop-api';

interface CommandPaletteProps {
  open: boolean;
  initialQuery: string;
  onAskAlice: (prompt?: string) => void;
  onOpenChange: (open: boolean) => void;
}

/**
 * The palette mirrors the navigation: the same five destinations, in the same
 * order, so `/goto` never offers a page the sidebar does not have. Settings and
 * Activity trail them because they exist and are worth reaching by keyboard —
 * they are simply not part of the daily loop.
 */
const NAV_COMMANDS = [
  { label: '/goto leadloop', route: '/leadloop', icon: <Repeat className="h-4 w-4" /> },
  { label: '/goto leads', route: '/leads', icon: <UserPlus className="h-4 w-4" /> },
  { label: '/goto form entries', route: '/forms-feed', icon: <ClipboardList className="h-4 w-4" /> },
  { label: '/goto calls', route: '/ai-systems/voice-agents', icon: <PhoneCall className="h-4 w-4" /> },
  { label: '/goto people', route: '/contacts', icon: <Users className="h-4 w-4" /> },
  { label: '/goto organizations', route: '/organizations', icon: <Building2 className="h-4 w-4" /> },
  { label: '/goto settings', route: '/settings', icon: <Settings className="h-4 w-4" /> },
  { label: '/goto activity', route: '/settings?tab=activity', icon: <Activity className="h-4 w-4" /> },
];

export function CommandPalette({ open, initialQuery, onAskAlice, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate();
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');

  const data = useQuery({
    queryKey: ['palette-data'],
    enabled: open,
    staleTime: 60_000,
    queryFn: async () => {
      const [leads, contacts, organizations] = await Promise.all([
        leadloopApi.leads({ status: 'all' }).catch(() => ({ items: [] })),
        leadloopApi.contacts().catch(() => ({ items: [] })),
        leadloopApi.organizations().catch(() => ({ items: [] })),
      ]);
      return { leads: leads.items, contacts: contacts.items, organizations: organizations.items };
    },
  });

  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(query), 50);
    return () => window.clearTimeout(handle);
  }, [query]);

  useEffect(() => {
    if (!open) return;
    previousFocusRef.current = document.activeElement as HTMLElement | null;
    setQuery(initialQuery);
    setDebounced(initialQuery);
  }, [initialQuery, open]);

  const close = () => {
    onOpenChange(false);
    window.setTimeout(() => previousFocusRef.current?.focus(), 0);
  };

  const navigateAndClose = (to: string) => {
    navigate(to);
    close();
  };
  const launchAlice = (prompt = '') => {
    close();
    onAskAlice(prompt);
  };

  const mode: 'search' | 'commands' | 'alice' = debounced.startsWith('?')
    ? 'alice'
    : debounced.startsWith('/')
      ? 'commands'
      : 'search';

  const q = debounced.trim().toLowerCase();
  const slashText = debounced.slice(1).toLowerCase();

  const results = useMemo(() => {
    if (!data.data || !q) return { leads: [], contacts: [], organizations: [] };
    const match = (s: string | undefined) => Boolean(s && s.toLowerCase().includes(q));
    return {
      leads: data.data.leads
        .filter((l) => match(l.title) || match(l.contactName) || match(l.companyName) || match(l.email))
        .slice(0, 6),
      contacts: data.data.contacts
        .filter((c) => match(c.fullName) || match(c.email) || match(c.phone))
        .slice(0, 5),
      organizations: data.data.organizations
        .filter((o) => match(o.name) || match(o.website) || match(o.industry))
        .slice(0, 5),
    };
  }, [data.data, q]);

  return (
    <Command.Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) close();
      }}
      label="Cascade command launcher"
      shouldFilter={false}
      className="fixed left-1/2 top-[max(8px,env(safe-area-inset-top))] z-[80] w-[calc(100vw-12px)] max-w-[680px] -translate-x-1/2 overflow-hidden rounded-lg border border-border bg-popover text-popover-foreground shadow-2xl sm:top-[14vh] sm:w-[min(680px,calc(100vw-32px))]"
    >
      <DialogTitle className="sr-only">Cascade command launcher</DialogTitle>
      <DialogDescription className="sr-only">
        Search leads, contacts, and organizations, run commands, or ask Alice.
      </DialogDescription>
      <div className="flex items-center gap-2 border-b border-border px-3">
        <Search className="h-4 w-4 text-muted-foreground" aria-hidden />
        <Command.Input
          value={query}
          onValueChange={setQuery}
          onKeyDown={(event) => {
            if ((event.metaKey || event.ctrlKey) && event.key === 'Enter' && query.startsWith('?')) {
              event.preventDefault();
              launchAlice(query.slice(1).trim());
            }
          }}
          placeholder="Search, type / for commands, or ? to ask Alice"
          className="h-12 flex-1 bg-transparent text-[16px] outline-none placeholder:text-muted-foreground sm:text-[14px]"
        />
      </div>

      <Command.List className="max-h-[min(72vh,520px)] overflow-y-auto p-2">
        {mode !== 'alice' && (
          <Command.Empty className="px-3 py-8 text-center text-sm text-muted-foreground">
            {data.isPending ? 'Loading…' : 'No results.'}
          </Command.Empty>
        )}

        {mode === 'search' && q.length === 0 && (
          <>
            <Command.Group heading="Alice">
              <Row
                value="alice"
                icon={<Sparkles className="h-4 w-4" />}
                label="Ask Alice…"
                secondary="Open the Alice assistant"
                onSelect={() => launchAlice()}
              />
            </Command.Group>
            <Command.Group heading="Go to">
              {NAV_COMMANDS.slice(0, 6).map((c) => (
                <Row
                  key={c.route}
                  value={c.label}
                  icon={c.icon}
                  label={c.label.replace('/goto ', '')}
                  onSelect={() => navigateAndClose(c.route)}
                />
              ))}
            </Command.Group>
            <div className="border-t border-border px-3 py-2 text-[11px] text-muted-foreground">
              Type / for commands, ? to ask Alice, or start typing to search
            </div>
          </>
        )}

        {mode === 'search' && q.length > 0 && (
          <>
            <Section heading="Leads">
              {results.leads.map((l) => (
                <Row
                  key={l.id}
                  value={l.id}
                  icon={<UserPlus className="h-4 w-4" />}
                  label={l.title}
                  secondary={[l.contactName, l.statusLabel].filter(Boolean).join(' · ')}
                  onSelect={() => navigateAndClose(`/leads/${l.id}`)}
                />
              ))}
            </Section>
            <Section heading="Contacts">
              {results.contacts.map((ct) => (
                <Row
                  key={ct.id}
                  value={ct.id}
                  icon={<Users className="h-4 w-4" />}
                  label={ct.fullName}
                  secondary={[ct.email, ct.phone].filter(Boolean).join(' · ')}
                  onSelect={() => navigateAndClose(`/contacts/${ct.id}`)}
                />
              ))}
            </Section>
            <Section heading="Organizations">
              {results.organizations.map((o) => (
                <Row
                  key={o.id}
                  value={o.id}
                  icon={<Building2 className="h-4 w-4" />}
                  label={o.name}
                  secondary={[o.industry, o.website].filter(Boolean).join(' · ')}
                  onSelect={() => navigateAndClose(`/organizations/${o.id}`)}
                />
              ))}
            </Section>
          </>
        )}

        {mode === 'commands' && (
          <Command.Group heading="Commands">
            {NAV_COMMANDS.filter((c) => c.label.slice(1).toLowerCase().includes(slashText)).map((c) => (
              <Row
                key={c.route}
                value={c.label}
                icon={c.icon}
                label={c.label}
                onSelect={() => navigateAndClose(c.route)}
              />
            ))}
          </Command.Group>
        )}

        {mode === 'alice' && (
          <div className="space-y-3 px-3 py-3">
            <div className="flex items-center gap-2 text-[12px] font-semibold text-primary">
              <Sparkles className="h-3.5 w-3.5" aria-hidden /> Ask Alice
            </div>
            <div className="rounded-md border border-border bg-muted/20 px-3 py-3 text-[12px] text-muted-foreground">
              Open Alice to keep the conversation going, scoped to the page you&apos;re on.
            </div>
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0 flex-1 truncate text-[12px] text-muted-foreground">
                {debounced.slice(1).trim() || 'Type ? followed by a question'}
              </div>
              <Button size="sm" className="gap-1.5" onClick={() => launchAlice(debounced.slice(1).trim())}>
                <Sparkles className="h-3.5 w-3.5" aria-hidden />
                {debounced.slice(1).trim() ? 'Send' : 'Open'}
              </Button>
            </div>
          </div>
        )}
      </Command.List>
    </Command.Dialog>
  );
}

function Section({ heading, children }: { heading: string; children: ReactNode }) {
  const items = Array.isArray(children) ? children : [children];
  if (items.filter(Boolean).length === 0) return null;
  return <Command.Group heading={heading}>{children}</Command.Group>;
}

function Row({
  value,
  icon,
  label,
  secondary,
  onSelect,
}: {
  value: string;
  icon: ReactNode;
  label: string;
  secondary?: string;
  onSelect: () => void;
}) {
  return (
    <Command.Item
      value={value}
      onSelect={onSelect}
      className={cn(
        'flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-[13px]',
        'aria-selected:bg-accent aria-selected:text-accent-foreground',
      )}
    >
      <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium">{label}</span>
        {secondary && <span className="block truncate text-[11px] text-muted-foreground">{secondary}</span>}
      </span>
    </Command.Item>
  );
}
